import { useContext, useEffect, useState } from "react"
import FilterContext from "../context/FilterContext"

const useFiltering = (data, pageName, additionalFilters = []) => {
  const { pageFilters, setFiltersForPage } = useContext(FilterContext)
  const [selectedFilters, setSelectedFilters] = useState(pageFilters[pageName] || {})

  const updateFilters = (filters) => {
    setSelectedFilters(filters)
    setFiltersForPage(pageName, filters)
  }

  useEffect(() => {
    setSelectedFilters(pageFilters[pageName] || {})
  }, [pageFilters, pageName])

  const filterData = (key, dependencyKeys = []) => {
    if (!data) return []
    return data.filter((item) => {
      return dependencyKeys.every((depKey) => {
        if (!selectedFilters[depKey] || selectedFilters[depKey].length === 0) {
          return true // No filter applied for this key
        }
        return selectedFilters[depKey].includes(item[depKey]?.toString())
      })
    })
  }

  const generateFilterOptions = (filterKey) => {
    let filteredData = data

    switch (filterKey) {
      case "client":
        filteredData = filterData(filterKey, ["product", "filterGroup", "material"])
        break
      case "product":
        filteredData = filterData(filterKey, ["client", "filterGroup", "material"])
        break
      case "filterGroup":
        filteredData = filterData(filterKey, ["client", "product", "material"])
        break
      case "material":
        filteredData = filterData(filterKey, ["client", "product", "filterGroup"])
        break
      case "templateName":
        filteredData = filterData(filterKey, ["client", "product", "filterGroup", "templateName"]) // No dependencies for templateName
        break
      case "machine":
        filteredData = filterData(filterKey, ["client", "product", "filterGroup", "material"])
        break
      default:
        break
    }

    return Array.from(
      new Set(
        filteredData.map((item) => {
          if (filterKey === "product") return item.product?.productName
          return item[filterKey]?.toString()
        })
      )
    )
      .filter((option) => option) // Filter out undefined or null values
      .map((option) => ({ text: option, value: option }))
  }

  //handles te filter change
  const handleTableFilterChange = (pagination, filters, sorter) => {
    // Initialize newFilters with the existing selectedFilters
    const newFilters = { ...selectedFilters }

    // Check and update each filter category
    ;["client", "product.productName", "filterGroup", "material", "templateName"].forEach(
      (filterKey) => {
        if (filters[filterKey] !== undefined) {
          newFilters[filterKey === "product.productName" ? "product" : filterKey] =
            filters[filterKey] || []
        }
      }
    )

    // If a filter category is cleared, set it to an empty array
    Object.keys(newFilters).forEach((key) => {
      if (newFilters[key] === undefined) {
        newFilters[key] = []
      }
    })

    // Update the filter state in both local state and context
    updateFilters(newFilters)
    setFiltersForPage(pageName, newFilters)
  }

  const filterOptions = {
    client: generateFilterOptions("client"),
    product: generateFilterOptions("product"),
    filterGroup: generateFilterOptions("filterGroup"),
    material: generateFilterOptions("material"),
    // Add templateName filter if included in additionalFilters
    ...(additionalFilters.includes("templateName") && {
      templateName: generateFilterOptions("templateName"),
    }),
  }

  return { selectedFilters, updateFilters, filterOptions, handleTableFilterChange }
}

export default useFiltering
